import React from 'react';
import type { Citation } from '../api/claims/interface';

export const OWL_AUTHOR_KEY = '__owl__';

const PRIORITY_RANK: Record<string, number> = {
  high: 0,
  med: 1,
  low: 2,
};

export interface OwlTaskOrderFields {
  status: string;
  priority: string;
  due: string | null;
  created_at: string | null;
}

// Order Owl-raised tasks for the home feed: open before done, then by
// priority, then earliest due date, then most recent first.
export function compareOwlTasks(
  a: OwlTaskOrderFields,
  b: OwlTaskOrderFields
): number {
  const isDone = (t: OwlTaskOrderFields) => t.status === 'done';
  if (isDone(a) !== isDone(b)) {
    return isDone(a) ? 1 : -1;
  }
  const pa = PRIORITY_RANK[a.priority] ?? PRIORITY_RANK.med;
  const pb = PRIORITY_RANK[b.priority] ?? PRIORITY_RANK.med;
  if (pa !== pb) {
    return pa - pb;
  }
  if (a.due && b.due && a.due !== b.due) {
    return a.due < b.due ? -1 : 1;
  }
  if (a.due !== b.due) {
    return a.due ? -1 : 1;
  }
  return (b.created_at ?? '').localeCompare(a.created_at ?? '');
}

// Returns a sentinel for 'agent' so callers can swap in a localized label
// (`messages['esc.owl_author']`) rather than the hardcoded English.
export function authorDisplay(author: string): string {
  return author === 'agent' ? OWL_AUTHOR_KEY : author;
}

export function useCitationHandler(
  onFileClick: (fileId: string, page?: number) => void,
  onKnowledgeClick?: (path: string, anchor?: string) => void
): (citation: Citation) => void {
  return React.useCallback(
    (citation: Citation) => {
      if (citation.fragment_type === 'KNOWLEDGE') {
        if (onKnowledgeClick && citation.path) {
          onKnowledgeClick(citation.path, citation.anchor);
        }
        return;
      }
      if (citation.file_id) {
        const page = citation.start_page ?? citation.pages?.[0];
        onFileClick(citation.file_id, page);
      }
    },
    [onFileClick, onKnowledgeClick]
  );
}
